import { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import { Link } from "wouter";
import { useUser } from "@clerk/react";
import {
  Check, Zap, Quote, LayoutList,
  Play, Pause, ChevronRight, Mic, Rss, Star, ArrowRight,
  Clock, Bell, Wifi, Video, Download, Crown, Image, Menu, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

// ── Sample demo data ─────────────────────────────────────────────────────────
// Source episode: "The Origin, Activity and Vision of Podcasts Abbreviated"
// (Podcasts Abbreviated, Episode 15) — hosts Tim Hedberg & Chris Meade,
// RSS https://rss.buzzsprout.com/1715522.rss
// Audio assets in /public/demo/ were generated by running this episode through
// the Podcasts Abbreviated pipeline. The five Key Quotes clips below were
// hand-picked by the team to showcase Key Quotes mode.

const DEMO_EPISODE_TITLE = "The Origin, Activity and Vision of Podcasts Abbreviated";

const DEMO_QUOTES = [
  {
    speaker: "Tim Hedberg",
    src: `${basePath}/demo/kq-1.mp3`,
    text: "There are so many podcasts out there, and every time I'd check my podcast roll, no matter how much I listened to, there were always people I couldn't get to, ideas I couldn't get to. I just wanted somebody to tell me what was in the latest Joe Rogan episode, what was the latest insight that came out of The Portal. I didn't want to have to listen to a two-hour podcast when at the end of the day there were probably three really good points I'm interested in.",
  },
  {
    speaker: "Chris Meade",
    src: `${basePath}/demo/kq-2.mp3`,
    text: "If I listen to a two-and-a-half-hour podcast and say to myself, wow, how I thought about that topic has now changed — and I do that 200 times — the next time I go into a conversation with somebody that I love and that knows me, the leaps of logic that I make are going to go past that person.",
  },
  {
    speaker: "Tim Hedberg",
    src: `${basePath}/demo/kq-3.mp3`,
    text: "This idea that podcasts themselves are underrated, but the format that they're in is overrated. These long conversations with interesting people — it's a lot to get through, and it can be kind of boring at times. But at the same time, there's no better place in the world today to access raw, interesting, unfiltered ideas that arise in a spontaneous format for the world to hear.",
  },
  {
    speaker: "Chris Meade",
    src: `${basePath}/demo/kq-4.mp3`,
    text: "That's the whole point of Podcasts Abbreviated — to provide the sentiments, the instructions, the knowledge. That shouldn't be something where it takes you an hour to break down to help you understand. It should be something that's so resonating that the moment you hear it, you think, yeah, I've done this all along.",
  },
  {
    speaker: "Tim Hedberg",
    src: `${basePath}/demo/kq-5.mp3`,
    text: "We want to give people the choice to find new and interesting thinkers. You and I both know it's really important that you find somebody that speaks to you. And I feel like so many people are walking through this life and they just haven't found the person for them that clicks.",
  },
];

const DEMO_MODES = [
  {
    id: "key_quotes",
    label: "Key Quotes",
    icon: <Quote size={14} />,
    featured: true,
    originalLabel: "20:15",
    src: `${basePath}/demo/key-quotes.mp3`,
    description: "AI-extracted highlight moments, perfect for social sharing",
  },
  {
    id: "quick_catchup",
    label: "Quick Catch-Up",
    icon: <Zap size={14} />,
    featured: false,
    originalLabel: "20:15",
    src: `${basePath}/demo/quick-catchup.mp3`,
    description: "20% of the runtime, 100% of the ideas",
  },
  {
    id: "full_recap",
    label: "Full Recap",
    icon: <LayoutList size={14} />,
    featured: false,
    originalLabel: "20:15",
    src: `${basePath}/demo/full-recap.mp3`,
    description: "50% compression, full narrative arc preserved",
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// ── Waveform visualization ────────────────────────────────────────────────────

function WaveformBars({ playing, visible }: { playing: boolean; visible: boolean }) {
  const bars = Array.from({ length: 32 });
  return (
    <div className="flex items-center justify-center gap-0.5 h-10">
      {bars.map((_, i) => (
        <div
          key={i}
          className={`w-1 rounded-full transition-all ${i % 7 === 0 ? "bg-primary" : "bg-primary/30"}`}
          style={{
            height: `${20 + Math.sin(i * 0.7) * 14 + (i % 3) * 5}px`,
            animation: playing && visible
              ? `waveBar ${0.5 + (i % 4) * 0.15}s ease-in-out infinite alternate`
              : "none",
            animationDelay: `${i * 0.03}s`,
          }}
        />
      ))}
    </div>
  );
}

// ── Playback state type shared between DemoPlayer and DemoMiniPlayer ──────────

interface PlaybackState {
  playing: boolean;
  currentTime: number;
  duration: number;
  trackName: string;
}

export interface DemoPlayerHandle {
  togglePlay: () => void;
}

// ── Demo player ───────────────────────────────────────────────────────────────

const DemoPlayer = forwardRef<DemoPlayerHandle, { visible: boolean; onStateChange?: (s: PlaybackState) => void }>(
  function DemoPlayer({ visible, onStateChange }, ref) {
  const [activeMode, setActiveMode] = useState("key_quotes");
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [activeKQClip, setActiveKQClip] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  // Refs so event-listener callbacks can see the latest values without re-binding
  const activeModeRef = useRef(activeMode);
  const activeKQClipRef = useRef(activeKQClip);
  const shouldAutoPlayRef = useRef(false);
  useEffect(() => { activeModeRef.current = activeMode; }, [activeMode]);
  useEffect(() => { activeKQClipRef.current = activeKQClip; }, [activeKQClip]);

  useEffect(() => {
    if (!visible) {
      const audio = audioRef.current;
      if (audio && !audio.paused) {
        audio.pause();
        setPlaying(false);
      }
    }
  }, [visible]);

  const mode = DEMO_MODES.find(m => m.id === activeMode)!;
  const isKQ = activeMode === "key_quotes";

  const trackName = isKQ
    ? `${DEMO_QUOTES[activeKQClip].speaker} — clip ${activeKQClip + 1}`
    : mode.label;

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Load the correct audio src whenever the KQ clip index changes
  useEffect(() => {
    if (!isKQ) return;
    const audio = audioRef.current;
    if (!audio) return;
    audio.src = DEMO_QUOTES[activeKQClip].src;
    audio.load();
    setCurrentTime(0);
    setDuration(0);
    if (shouldAutoPlayRef.current) {
      audio.play().catch(() => {});
      setPlaying(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKQClip, isKQ]);

  const switchMode = useCallback((modeId: string) => {
    const audio = audioRef.current;
    if (audio) { audio.pause(); audio.currentTime = 0; }
    shouldAutoPlayRef.current = false;
    setActiveMode(modeId);
    setActiveKQClip(0);
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, []);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
    } else {
      audio.play().catch(() => {});
    }
    setPlaying(p => !p);
  }, [playing]);

  useImperativeHandle(ref, () => ({ togglePlay }), [togglePlay]);

  useEffect(() => {
    onStateChange?.({ playing, currentTime, duration, trackName });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, currentTime, duration, trackName]);

  const handleSeek = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const pct = Number(e.target.value);
    const newTime = (pct / 100) * duration;
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  }, [duration]);

  // For non-KQ modes: seek by percentage within the single file
  const seekToQuote = useCallback((pct: number) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    audio.currentTime = (pct / 100) * duration;
    setCurrentTime((pct / 100) * duration);
    audio.play().catch(() => {});
    setPlaying(true);
  }, [duration]);

  // For KQ mode: select a specific clip and play it
  const selectKQClip = useCallback((idx: number) => {
    shouldAutoPlayRef.current = true;
    if (activeKQClipRef.current === idx) {
      // Same clip — just play/restart from beginning
      const audio = audioRef.current;
      if (audio) { audio.currentTime = 0; audio.play().catch(() => {}); }
      setPlaying(true);
    } else {
      setActiveKQClip(idx);
    }
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => setCurrentTime(audio.currentTime);
    const onDuration = () => setDuration(audio.duration);
    const onEnded = () => {
      if (activeModeRef.current === "key_quotes") {
        const next = activeKQClipRef.current + 1;
        if (next < DEMO_QUOTES.length) {
          shouldAutoPlayRef.current = true;
          setActiveKQClip(next);
        } else {
          shouldAutoPlayRef.current = false;
          setPlaying(false);
          setCurrentTime(0);
          setActiveKQClip(0);
        }
      } else {
        setPlaying(false);
        setCurrentTime(0);
      }
    };
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onDuration);
    audio.addEventListener("durationchange", onDuration);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onDuration);
      audio.removeEventListener("durationchange", onDuration);
      audio.removeEventListener("ended", onEnded);
    };
  }, [activeMode]);

  return (
    <div className="rounded-xl border border-border/50 bg-card/60 backdrop-blur-sm overflow-hidden shadow-2xl shadow-primary/5">
      {/* Hidden audio element — src managed imperatively for KQ per-clip mode */}
      <audio ref={audioRef} src={isKQ ? DEMO_QUOTES[activeKQClip].src : mode.src} preload="metadata" />

      <div className="p-4 border-b border-border/30 flex items-center justify-between">
        <span className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Live Demo</span>
        <span className="text-xs font-mono text-primary bg-primary/10 px-2 py-0.5 rounded border border-primary/20">Interactive</span>
      </div>

      {/* Mode tabs */}
      <div className="flex border-b border-border/30">
        {DEMO_MODES.map(m => (
          <button
            key={m.id}
            onClick={() => switchMode(m.id)}
            className={`flex-1 flex flex-col items-center gap-1 py-3 px-2 text-xs font-mono uppercase tracking-wider transition-colors relative ${
              activeMode === m.id
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:text-foreground hover:bg-secondary/30"
            }`}
          >
            {m.featured && activeMode !== m.id && (
              <span className="absolute -top-px left-1/2 -translate-x-1/2 text-[9px] text-amber-400 font-mono">★ Featured</span>
            )}
            <span className="flex items-center gap-1">{m.icon} {m.label}</span>
            {activeMode === m.id && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
            )}
          </button>
        ))}
      </div>

      <div className="p-5 space-y-5">
        {/* Duration info */}
        <div className="flex items-center justify-between text-xs font-mono text-muted-foreground">
          <div className="flex items-center gap-2">
            <span className="line-through opacity-50">{mode.originalLabel}</span>
            <ArrowRight size={10} />
            <span className="text-primary font-bold">
              {duration > 0 ? fmtTime(duration) : "—:——"}
            </span>
          </div>
          <Badge variant="outline" className="text-[10px] border-emerald-500/30 text-emerald-400">
            real audio
          </Badge>
        </div>

        {/* Waveform */}
        <WaveformBars playing={playing} visible={visible} />

        {/* Seek bar */}
        <div className="space-y-2">
          <div className="relative h-1.5">
            <div className="absolute inset-0 bg-secondary rounded-full" />
            <div
              className="absolute left-0 top-0 h-full bg-primary rounded-full pointer-events-none"
              style={{ width: `${progress}%` }}
            />
            <input
              type="range"
              min={0}
              max={100}
              step={0.1}
              value={progress}
              onChange={handleSeek}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
          </div>
          <div className="flex justify-between text-xs font-mono text-muted-foreground">
            <span>{fmtTime(currentTime)}</span>
            <span>{duration > 0 ? fmtTime(duration) : "—:——"}</span>
          </div>
        </div>

        {/* Play button */}
        <div className="flex items-center justify-center">
          <button
            onClick={togglePlay}
            className="h-12 w-12 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground flex items-center justify-center transition-transform hover:scale-105 active:scale-95 shadow-lg shadow-primary/30"
          >
            {playing
              ? <Pause size={20} fill="currentColor" />
              : <Play size={20} fill="currentColor" className="ml-0.5" />}
          </button>
        </div>

        {/* Mode-specific content */}
        {activeMode === "key_quotes" && (
          <div className="space-y-2 mt-2">
            <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">Key moments extracted · click to play</p>
            <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
              {DEMO_QUOTES.map((q, i) => {
                const isActiveClip = activeKQClip === i;
                return (
                  <div
                    key={i}
                    className={`flex gap-3 p-3 rounded-lg border transition-all cursor-pointer ${
                      isActiveClip
                        ? "border-primary/50 bg-primary/10"
                        : "border-border/30 bg-secondary/20 hover:border-border/60"
                    }`}
                    onClick={() => selectKQClip(i)}
                  >
                    <div className="shrink-0 flex items-start mt-0.5">
                      <span className={`text-[10px] font-mono w-4 ${isActiveClip && playing ? "text-primary" : "text-muted-foreground"}`}>
                        {isActiveClip && playing ? "▶" : `${i + 1}`}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-[10px] font-mono text-muted-foreground mb-0.5">{q.speaker}</div>
                      <p className="text-xs text-foreground/90 leading-relaxed line-clamp-2">"{q.text}"</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeMode === "quick_catchup" && (
          <div className="bg-secondary/30 rounded-lg p-4 border border-border/30">
            <p className="text-xs text-muted-foreground font-mono leading-relaxed">
              Fast-forward through the whole episode. Filler, tangents, and small talk removed. Only the substance remains. Every key idea in 20% of the runtime.
            </p>
          </div>
        )}

        {activeMode === "full_recap" && (
          <div className="bg-secondary/30 rounded-lg p-4 border border-border/30">
            <p className="text-xs text-muted-foreground font-mono leading-relaxed">
              The complete narrative, condensed. Stories flow naturally, context preserved. Like being there, at half the time commitment.
            </p>
          </div>
        )}
      </div>

      <div className="px-5 pb-5">
        <Link href="/upload">
          <Button className="w-full font-mono uppercase text-xs tracking-wider" size="sm">
            Upload your episode <ChevronRight size={14} className="ml-1" />
          </Button>
        </Link>
      </div>
    </div>
  );
});

// ── Demo mini-player (sticky footer bar) ─────────────────────────────────────

function DemoMiniPlayer({
  state,
  onTogglePlay,
  onDismiss,
}: {
  state: PlaybackState;
  onTogglePlay: () => void;
  onDismiss: () => void;
}) {
  const { playing, currentTime, duration, trackName } = state;
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-border/60 bg-background/95 backdrop-blur-md shadow-2xl shadow-black/20"
      style={{ transform: "translateZ(0)" }}
    >
      <div className="max-w-6xl mx-auto px-4 h-14 flex items-center gap-4">
        <button
          onClick={onTogglePlay}
          className="shrink-0 h-9 w-9 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground flex items-center justify-center transition-transform hover:scale-105 active:scale-95 shadow-md shadow-primary/30"
          aria-label={playing ? "Pause demo" : "Play demo"}
        >
          {playing
            ? <Pause size={15} fill="currentColor" />
            : <Play size={15} fill="currentColor" className="ml-0.5" />}
        </button>

        <div className="flex-1 min-w-0 space-y-1">
          <p className="text-xs font-mono text-foreground/80 truncate leading-none">
            <span className="text-muted-foreground">Live Demo · </span>{trackName}
          </p>
          <div className="relative h-1 w-full">
            <div className="absolute inset-0 bg-secondary rounded-full" />
            <div
              className="absolute left-0 top-0 h-full bg-primary rounded-full pointer-events-none transition-none"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="shrink-0 flex items-center gap-2 text-xs font-mono text-muted-foreground tabular-nums">
          <span>{fmtTime(currentTime)}</span>
          <span className="opacity-40">/</span>
          <span>{duration > 0 ? fmtTime(duration) : "—:——"}</span>
        </div>

        <button
          onClick={onDismiss}
          className="shrink-0 h-7 w-7 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
          aria-label="Dismiss demo player"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

// ── Landing nav ───────────────────────────────────────────────────────────────

const NAV_LINKS = [
  { label: "How it works", id: "demo" },
  { label: "Pricing", id: "pricing" },
];

function LandingNav() {
  const { isSignedIn } = useUser();
  const [mobileOpen, setMobileOpen] = useState(false);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const wasOpen = useRef(false);

  useEffect(() => {
    if (!mobileOpen && wasOpen.current) {
      toggleButtonRef.current?.focus();
    }
    wasOpen.current = mobileOpen;
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return;

    const FOCUSABLE = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    const getFocusable = () =>
      drawerRef.current
        ? Array.from(drawerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE))
        : [];

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMobileOpen(false);
        return;
      }
      if (e.key === "Tab") {
        const focusable = getFocusable();
        if (focusable.length === 0) { e.preventDefault(); return; }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        const insideDrawer = drawerRef.current?.contains(active) ?? false;
        if (e.shiftKey) {
          if (active === first || !insideDrawer) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (active === last || !insideDrawer) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    const handleScroll = () => setMobileOpen(false);

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("keydown", handleKeyDown);

    const focusable = getFocusable();
    if (focusable.length > 0) focusable[0].focus();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [mobileOpen]);

  function scrollTo(id: string) {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileOpen(false);
  }

  return (
    <>
    {mobileOpen && (
      <div
        className="fixed inset-0 z-40 sm:hidden"
        onClick={() => setMobileOpen(false)}
        aria-hidden="true"
      />
    )}
    <header className="border-b border-border/50 bg-background/80 backdrop-blur-sm sticky top-0 z-50">
      <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2 group">
          <img
            src={`${basePath}/pa-icon.jpg`}
            alt="Podcasts Abbreviated"
            className="h-8 w-8 rounded-sm object-cover group-hover:scale-105 transition-transform"
          />
          <span className="font-mono font-bold tracking-tight uppercase text-foreground">
            Podcasts <span className="text-primary">Abbreviated</span>
          </span>
        </Link>

        <nav className="flex items-center gap-2">
          <div className="hidden sm:flex items-center gap-1 mr-3 pr-3 border-r border-border/50">
            {NAV_LINKS.map((link, idx) => (
              <a
                key={link.id}
                href={`#${link.id}`}
                className={[
                  "inline-flex items-center px-3 py-1.5 rounded-md font-mono uppercase text-xs tracking-wider",
                  "text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors",
                  idx > 0 ? "ml-1" : "",
                ].join(" ")}
                onClick={(e) => {
                  e.preventDefault();
                  scrollTo(link.id);
                }}
              >
                {link.label}
              </a>
            ))}
          </div>
          {isSignedIn ? (
            <Link href="/upload">
              <Button variant="outline" size="sm" className="font-mono uppercase text-xs tracking-wider">
                <Mic size={14} className="mr-1.5" /> Dashboard
              </Button>
            </Link>
          ) : (
            <>
              <Link href="/sign-in">
                <Button variant="ghost" size="sm" className="font-mono uppercase text-xs tracking-wider text-muted-foreground hover:text-foreground">
                  Sign In
                </Button>
              </Link>
              <Link href="/sign-up">
                <Button size="sm" className="font-mono uppercase text-xs tracking-wider hidden sm:inline-flex">
                  Get Started Free
                </Button>
              </Link>
            </>
          )}
          <button
            ref={toggleButtonRef}
            className="sm:hidden flex items-center justify-center p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-menu"
            onClick={() => setMobileOpen((o) => !o)}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </nav>
      </div>

      {mobileOpen && (
        <div
          id="mobile-menu"
          ref={drawerRef}
          className="sm:hidden border-t border-border/50 bg-background/95 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation menu"
        >
          <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <a
                key={link.id}
                href={`#${link.id}`}
                className="font-mono uppercase text-xs tracking-wider text-muted-foreground hover:text-foreground transition-colors py-2 px-1 border-b border-border/30 last:border-0"
                onClick={(e) => {
                  e.preventDefault();
                  scrollTo(link.id);
                }}
              >
                {link.label}
              </a>
            ))}
            <Link
              href="/help"
              className="font-mono uppercase text-xs tracking-wider text-muted-foreground hover:text-foreground transition-colors py-2 px-1 border-b border-border/30"
              onClick={() => setMobileOpen(false)}
            >
              Help
            </Link>
            {!isSignedIn && (
              <div className="pt-2">
                <Link href="/sign-up" onClick={() => setMobileOpen(false)}>
                  <Button size="sm" className="font-mono uppercase text-xs tracking-wider w-full">
                    Get Started Free
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
    </>
  );
}

// ── Main landing page ─────────────────────────────────────────────────────────

export default function LandingPage() {
  const phoneMockupRef = useRef<HTMLDivElement>(null);
  const [waveformVisible, setWaveformVisible] = useState(true);

  useEffect(() => {
    const el = phoneMockupRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setWaveformVisible(entry.isIntersecting),
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const heroPlayerRef = useRef<HTMLDivElement>(null);
  const [heroPlayerVisible, setHeroPlayerVisible] = useState(true);
  const [miniPlayerDismissed, setMiniPlayerDismissed] = useState(
    () => sessionStorage.getItem("demoMiniPlayerDismissed") === "true"
  );
  const demoPlayerRef = useRef<DemoPlayerHandle>(null);
  const [demoState, setDemoState] = useState<PlaybackState>({
    playing: false,
    currentTime: 0,
    duration: 0,
    trackName: `${DEMO_QUOTES[0].speaker} — clip 1`,
  });

  useEffect(() => {
    const el = heroPlayerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setHeroPlayerVisible(entry.isIntersecting),
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const rssSectionRef = useRef<HTMLElement>(null);
  const [rssSectionVisible, setRssSectionVisible] = useState(true);
  const [episodeCompleted, setEpisodeCompleted] = useState(false);

  useEffect(() => {
    const el = rssSectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setRssSectionVisible(entry.isIntersecting),
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!rssSectionVisible) {
      setEpisodeCompleted(false);
      return;
    }
    let t: ReturnType<typeof setTimeout>;
    const cycle = (completed: boolean) => {
      const delay = completed ? 3500 : 2500;
      t = setTimeout(() => {
        setEpisodeCompleted(!completed);
        cycle(!completed);
      }, delay);
    };
    cycle(false);
    return () => clearTimeout(t);
  }, [rssSectionVisible]);

  return (
    <div className="min-h-screen bg-background">
      <LandingNav />

      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-6xl mx-auto px-4 pt-20 pb-16 md:pt-28 md:pb-24">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="text-4xl md:text-5xl font-mono font-bold tracking-tight text-foreground leading-tight">
                Every episode,{" "}
                <span className="text-primary">automatically distilled into clips and social posts</span>
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Connect your RSS feed once. We distill each new episode into powerful quote clips, captioned videos, and auto-posts to YouTube and LinkedIn while you focus on making the next one.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link href="/upload">
                  <Button size="lg" className="font-mono uppercase tracking-wider w-full sm:w-auto">
                    Try it on your podcast <ChevronRight size={16} className="ml-1" />
                  </Button>
                </Link>
              </div>
              <p className="text-xs text-muted-foreground font-mono">
                5 free episodes/month · No credit card · Connect your RSS in 30 seconds
              </p>
            </div>
            <div className="relative" ref={heroPlayerRef}>
              <div className="absolute -inset-4 bg-primary/5 rounded-2xl blur-2xl" />
              <div className="relative space-y-4">
                <div className="space-y-1.5">
                  <h2 className="text-xl md:text-2xl font-mono font-bold tracking-tight text-foreground">
                    See it work on a real podcast
                  </h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Below is a real episode from our own show. Watch the system pull out the most powerful moments and turn them into clips ready for social.
                  </p>
                </div>
                <DemoPlayer
                  ref={demoPlayerRef}
                  visible={heroPlayerVisible}
                  onStateChange={setDemoState}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Key Quotes feature callout ─────────────────────────────────── */}
      <section id="demo" className="py-20 border-t border-border/30">
        <div className="max-w-6xl mx-auto px-4">
          <div className="rounded-2xl border border-primary/20 bg-primary/5 p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div className="space-y-5">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-400/30 bg-amber-400/10 text-amber-400 text-xs font-mono uppercase tracking-wider">
                  <Star size={10} fill="currentColor" /> Featured Mode
                </div>
                <h2 className="text-3xl font-mono font-bold tracking-tight">
                  Key Quotes —<br />
                  <span className="text-primary">share your best moments instantly</span>
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  Pulls out the most powerful moments from each episode, with timestamps and speaker attribution. Each clip stands on its own, ready to post. Perfect for growing your audience between episodes.
                </p>
                <ul className="space-y-2">
                  {[
                    "Timestamped clips ready to share",
                    "Speaker-attributed quotes",
                    "Navigable from a visual quote list",
                    "Download individual clips (Pro)",
                  ].map(f => (
                    <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Check size={14} className="text-primary shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Link href="/upload">
                  <Button className="font-mono uppercase text-xs tracking-wider">
                    Try Key Quotes Free <ChevronRight size={14} className="ml-1" />
                  </Button>
                </Link>
              </div>
              <div className="space-y-3">
                <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Sample extracted moments</p>
                {DEMO_QUOTES.slice(0, 3).map((q, i) => (
                  <div key={i} className="flex gap-3 p-4 rounded-lg border border-border/30 bg-card/40 hover:border-primary/30 transition-colors">
                    <div className="shrink-0">
                      <span className="text-xs font-mono text-primary bg-primary/10 px-2 py-0.5 rounded border border-primary/20">
                        clip {i + 1}
                      </span>
                    </div>
                    <div>
                      <div className="text-[10px] font-mono text-muted-foreground mb-1">{q.speaker}</div>
                      <p className="text-sm text-foreground/90 leading-relaxed">"{q.text}"</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── RSS Auto-Processing Feature ────────────────────────────────── */}
      <section id="rss" className="py-20 border-t border-border/30" ref={rssSectionRef}>
        <div className="max-w-6xl mx-auto px-4">
          <div className="rounded-2xl border border-primary/20 bg-primary/5 p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div className="space-y-5">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/30 bg-primary/10 text-primary text-xs font-mono uppercase tracking-wider">
                  <Rss size={10} /> Pro Feature
                </div>
                <h2 className="text-3xl font-mono font-bold tracking-tight">
                  <span className="text-primary">Set it up once,</span>{" "}
                  <span className="text-foreground">never think about it again</span>
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  Add your podcast's RSS feed once. From then on, every new episode you publish gets clipped, captioned, turned into social videos, and auto-posted to YouTube and LinkedIn. Automatically, within an hour of publishing.
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Auto-posts to</span>
                  {/* YouTube */}
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-border/40 bg-card/40 text-xs font-mono text-foreground/80">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="#FF0000">
                      <path d="M23.5 6.19a3.02 3.02 0 00-2.12-2.14C19.54 3.5 12 3.5 12 3.5s-7.54 0-9.38.55A3.02 3.02 0 00.5 6.19 31.73 31.73 0 000 12a31.73 31.73 0 00.5 5.81 3.02 3.02 0 002.12 2.14C4.46 20.5 12 20.5 12 20.5s7.54 0 9.38-.55a3.02 3.02 0 002.12-2.14A31.73 31.73 0 0024 12a31.73 31.73 0 00-.5-5.81zM9.75 15.02V8.98L15.5 12l-5.75 3.02z"/>
                    </svg>
                    YouTube
                  </div>
                  {/* LinkedIn */}
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-border/40 bg-card/40 text-xs font-mono text-foreground/80">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="#0A66C2">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    LinkedIn
                  </div>
                </div>
                <ul className="space-y-2">
                  {[
                    "Works with any podcast RSS feed URL",
                    "New episodes detected and processed automatically",
                    "Auto-posted to YouTube and LinkedIn",
                    "Email notification when each abbreviation is ready",
                    "Manage multiple feed subscriptions at once",
                    "Pause or cancel any feed at any time",
                  ].map(f => (
                    <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Check size={14} className="text-primary shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Link href="/sign-up">
                  <Button className="font-mono uppercase text-xs tracking-wider">
                    Start Pro Free Trial <ChevronRight size={14} className="ml-1" />
                  </Button>
                </Link>
              </div>

              {/* RSS Feed mock visual */}
              <div className="rounded-xl border border-border/40 bg-card/60 backdrop-blur-sm overflow-hidden shadow-xl shadow-primary/5">
                <div className="p-4 border-b border-border/30 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Rss size={14} className="text-primary" />
                    <span className="text-xs font-mono text-foreground font-bold">Podcasts Abbreviated</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px] font-mono text-emerald-400">
                    <Wifi size={10} />
                    <span>Active</span>
                  </div>
                </div>

                <div className="divide-y divide-border/20">
                  {/* Newest episode — processing → completed */}
                  <div
                    className={`px-4 py-3 border-l-2 transition-colors duration-700 ${episodeCompleted ? "bg-emerald-500/5 border-emerald-500/50" : "bg-primary/5 border-primary"}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-mono text-foreground truncate">The Origin, Activity and Vision of Podcasts Abbreviated</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5 font-mono">20 min · just published</p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {episodeCompleted ? (
                          <>
                            <span className="animate-in fade-in duration-500 text-[10px] font-mono px-2 py-0.5 rounded border bg-emerald-500/10 border-emerald-500/30 text-emerald-400">
                              Abbreviated ✓
                            </span>
                            <span className="animate-in fade-in duration-700 flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border bg-red-500/10 border-red-500/20 text-red-400">
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                              Posted ✓
                            </span>
                            <span className="animate-in fade-in duration-1000 flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border bg-blue-500/10 border-blue-500/20 text-blue-400">
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                              Posted ✓
                            </span>
                          </>
                        ) : (
                          <span className="text-[10px] font-mono px-2 py-0.5 rounded border bg-amber-400/10 border-amber-400/30 text-amber-400 flex items-center gap-1">
                            <span
                              className="inline-block h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse"
                              style={{ animationPlayState: rssSectionVisible ? "running" : "paused" }}
                            />
                            Processing
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Episode 2 — done */}
                  <div className="px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-mono text-foreground truncate">Grief Like You've Never Heard It</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5 font-mono">38 min · 3 months ago</p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded border bg-emerald-500/10 border-emerald-500/30 text-emerald-400">
                          Abbreviated ✓
                        </span>
                        <span className="flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border bg-red-500/10 border-red-500/20 text-red-400">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                          Posted ✓
                        </span>
                        <span className="flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border bg-blue-500/10 border-blue-500/20 text-blue-400">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                          Posted ✓
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Episode 3 — done */}
                  <div className="px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-mono text-foreground truncate">Ep. 18 | Francis Weller and The Wild Edge of Sorrow</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5 font-mono">40 min · 4 months ago</p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded border bg-emerald-500/10 border-emerald-500/30 text-emerald-400">
                          Abbreviated ✓
                        </span>
                        <span className="flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border bg-red-500/10 border-red-500/20 text-red-400">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                          Posted ✓
                        </span>
                        <span className="flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border bg-blue-500/10 border-blue-500/20 text-blue-400">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                          Posted ✓
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="px-4 py-3 border-t border-border/20 flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-1.5 text-[10px] font-mono text-muted-foreground">
                    <Clock size={10} />
                    <span>Checks for new episodes hourly</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 text-[10px] font-mono text-red-400">
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor" className="text-blue-400" aria-hidden="true"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                      <span className="text-muted-foreground">Auto-post on</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] font-mono text-muted-foreground">
                      <Bell size={10} />
                      <span>Email alerts on</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Share to Social ─────────────────────────────────────────────── */}
      <section id="video" className="py-20 border-t border-border/30">
        <div className="max-w-6xl mx-auto px-4">
          <div className="rounded-2xl border border-primary/20 bg-primary/5 p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-10 items-center">

              {/* Left: copy */}
              <div className="space-y-5">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/30 bg-primary/10 text-primary text-xs font-mono uppercase tracking-wider">
                  <Video size={10} /> New Feature
                </div>
                <h2 className="text-3xl font-mono font-bold tracking-tight">
                  Abbreviated audio →<br />
                  <span className="text-primary">shareable portrait video</span>
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  Turn any abbreviated clip into a polished portrait video with captions and artwork, automatically published to your social channels every time a new episode drops.
                </p>

                {/* Platform row */}
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Post to</span>
                  {/* TikTok */}
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-border/40 bg-card/40 text-xs font-mono text-foreground/80">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" className="text-[#69C9D0]">
                      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.95a8.25 8.25 0 004.83 1.54V7.05a4.85 4.85 0 01-1.06-.36z"/>
                    </svg>
                    TikTok
                  </div>
                  {/* Instagram */}
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-border/40 bg-card/40 text-xs font-mono text-foreground/80">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="url(#ig-grad)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <defs>
                        <linearGradient id="ig-grad" x1="0" y1="24" x2="24" y2="0" gradientUnits="userSpaceOnUse">
                          <stop offset="0%" stopColor="#F58529"/>
                          <stop offset="50%" stopColor="#DD2A7B"/>
                          <stop offset="100%" stopColor="#8134AF"/>
                        </linearGradient>
                      </defs>
                      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                      <circle cx="12" cy="12" r="4"/>
                      <circle cx="17.5" cy="6.5" r="1" fill="url(#ig-grad)" stroke="none"/>
                    </svg>
                    Reels
                  </div>
                  {/* YouTube Shorts */}
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-border/40 bg-card/40 text-xs font-mono text-foreground/80">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="#FF0000">
                      <path d="M23.5 6.19a3.02 3.02 0 00-2.12-2.14C19.54 3.5 12 3.5 12 3.5s-7.54 0-9.38.55A3.02 3.02 0 00.5 6.19 31.73 31.73 0 000 12a31.73 31.73 0 00.5 5.81 3.02 3.02 0 002.12 2.14C4.46 20.5 12 20.5 12 20.5s7.54 0 9.38-.55a3.02 3.02 0 002.12-2.14A31.73 31.73 0 0024 12a31.73 31.73 0 00-.5-5.81zM9.75 15.02V8.98L15.5 12l-5.75 3.02z"/>
                    </svg>
                    Shorts
                  </div>
                  {/* X / Twitter */}
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-border/40 bg-card/40 text-xs font-mono text-foreground/80">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    X
                  </div>
                  {/* LinkedIn */}
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-border/40 bg-card/40 text-xs font-mono text-foreground/80">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="#0A66C2">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    LinkedIn
                  </div>
                </div>

                {/* 3-step flow */}
                <div className="pt-2">
                  <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-3">How it works</p>
                  <div className="flex items-start gap-2">
                    {[
                      { icon: <Zap size={14} />, label: "Abbreviate", sub: "Extract the best moments" },
                      { icon: <Image size={14} />, label: "Choose image", sub: "Artwork or AI-generated" },
                      { icon: <Download size={14} />, label: "Post to social", sub: "Auto-published every episode" },
                    ].map((step, i) => (
                      <div key={step.label} className="flex items-start gap-2 flex-1">
                        <div className="flex flex-col items-center gap-1.5 flex-1 min-w-0">
                          <div className="h-8 w-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shrink-0">
                            {step.icon}
                          </div>
                          <div className="text-center">
                            <p className="text-[11px] font-mono font-bold text-foreground">{step.label}</p>
                            <p className="text-[9px] font-mono text-muted-foreground leading-tight mt-0.5">{step.sub}</p>
                          </div>
                        </div>
                        {i < 2 && (
                          <div className="text-muted-foreground/40 mt-2 text-xs shrink-0">→</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pro callout for AI image */}
                <div className="flex items-center gap-2 p-3 rounded-lg border border-amber-400/20 bg-amber-400/5">
                  <Crown size={12} className="text-amber-400 shrink-0" />
                  <p className="text-xs font-mono text-muted-foreground">
                    <span className="text-amber-400 font-bold">Pro:</span> Generate a custom AI image per episode. No artwork needed.
                  </p>
                </div>

                <Link href="/sign-up">
                  <Button className="font-mono uppercase text-xs tracking-wider gap-2">
                    <Video size={14} />
                    Try it now
                  </Button>
                </Link>
              </div>

              {/* Right: phone mockup */}
              <div className="flex justify-center" ref={phoneMockupRef}>
                <div className="relative">
                  {/* Glow */}
                  <div className="absolute -inset-6 bg-primary/10 rounded-3xl blur-2xl pointer-events-none" />

                  {/* Phone frame */}
                  <div className="relative w-52 rounded-[2rem] border-2 border-border/60 bg-card/80 shadow-2xl shadow-primary/10 overflow-hidden"
                    style={{ aspectRatio: "9/16" }}>

                    {/* Status bar */}
                    <div className="absolute top-0 inset-x-0 h-6 bg-black/60 flex items-center justify-center z-10">
                      <div className="w-16 h-3 rounded-full bg-black/80 border border-border/20" />
                    </div>

                    {/* Background image (blurred gradient) */}
                    <div className="absolute inset-0 bg-gradient-to-b from-slate-800 via-slate-900 to-black">
                      <div className="absolute inset-0 opacity-30"
                        style={{
                          background: "radial-gradient(ellipse at 60% 30%, hsl(239 84% 67% / 0.4) 0%, transparent 70%)",
                        }}
                      />
                      {/* Artwork placeholder squares */}
                      <div className="absolute top-8 left-0 right-0 bottom-0 flex items-center justify-center opacity-20">
                        <div className="w-24 h-24 rounded-xl border border-primary/40 bg-primary/10" />
                      </div>
                    </div>

                    {/* Waveform decoration */}
                    <div className="absolute bottom-28 inset-x-0 flex items-end justify-center gap-0.5 px-6 h-10">
                      {Array.from({ length: 22 }).map((_, i) => (
                        <div
                          key={i}
                          className="w-1 rounded-full bg-primary/60"
                          style={{
                            height: `${8 + Math.sin(i * 0.9) * 7 + (i % 3) * 3}px`,
                            animation: `waveBar ${0.5 + (i % 4) * 0.15}s ease-in-out infinite alternate`,
                            animationDelay: `${i * 0.03}s`,
                            animationPlayState: waveformVisible ? "running" : "paused",
                          }}
                        />
                      ))}
                    </div>

                    {/* Caption overlay — subtitle lines */}
                    <div className="absolute bottom-14 inset-x-0 px-4 space-y-1">
                      {[
                        { text: "There are so many podcasts —", full: true },
                        { text: "ideas I couldn't get to.", full: false },
                      ].map((line, i) => (
                        <div key={i} className="flex justify-center">
                          <span
                            className={`text-white font-bold text-center leading-tight px-1 py-0.5 rounded`}
                            style={{
                              fontSize: "9px",
                              background: i === 0 ? "rgba(0,0,0,0.75)" : "rgba(99,102,241,0.8)",
                              display: "inline-block",
                            }}
                          >
                            {line.text}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Bottom bar — podcast name + play button */}
                    <div className="absolute bottom-0 inset-x-0 bg-black/70 backdrop-blur-sm px-3 py-2 flex items-center justify-between border-t border-border/20">
                      <div className="min-w-0">
                        <p className="text-white text-[8px] font-mono font-bold truncate">{DEMO_EPISODE_TITLE}</p>
                        <p className="text-white/50 text-[7px] font-mono truncate">Key Quotes · 1:49</p>
                      </div>
                      <div className="h-6 w-6 rounded-full bg-primary flex items-center justify-center shrink-0">
                        <Play size={8} fill="white" className="text-white ml-0.5" />
                      </div>
                    </div>

                    {/* Watermark label */}
                    <div className="absolute top-8 right-2 z-10">
                      <span className="text-[7px] font-mono text-white/40 uppercase tracking-wider">Preview</span>
                    </div>
                  </div>

                  {/* Format badges */}
                  <div className="absolute -right-14 top-1/2 -translate-y-1/2 flex flex-col gap-2">
                    {[
                      { label: "9:16", desc: "Portrait", active: true },
                      { label: "1:1", desc: "Square", active: false },
                      { label: "16:9", desc: "Landscape", active: false },
                    ].map(fmt => (
                      <div
                        key={fmt.label}
                        className={`px-2 py-1 rounded border text-center ${
                          fmt.active
                            ? "border-primary/50 bg-primary/10 text-primary"
                            : "border-border/30 bg-card/30 text-muted-foreground"
                        }`}
                      >
                        <p className="text-[9px] font-mono font-bold">{fmt.label}</p>
                        <p className="text-[7px] font-mono opacity-70">{fmt.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing ────────────────────────────────────────────────────── */}
      <section id="pricing" className="py-20 border-t border-border/30">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-2xl font-mono font-bold tracking-tight uppercase">Pricing</h2>
            <p className="text-muted-foreground mt-2 text-sm">Most weekly podcasters use Pro. Start free to see how it works on your show.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Free */}
            <div className="rounded-xl border border-border/50 bg-card/40 p-8 space-y-6">
              <div>
                <h3 className="font-mono font-bold uppercase tracking-wider text-foreground">Free</h3>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-4xl font-mono font-bold">$0</span>
                  <span className="text-muted-foreground text-sm">/month</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1 font-mono">No credit card required</p>
              </div>
              <ul className="space-y-3">
                {[
                  "5 episodes / month",
                  "1 hr max episode length",
                  "All 3 modes (Key Quotes, Quick Catch-Up, Full Recap)",
                  "MP3 download",
                  "Social video export (portrait, landscape, square)",
                  "30-day result storage",
                ].map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Check size={14} className="text-primary shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Link href="/upload">
                <Button variant="outline" className="w-full font-mono uppercase text-xs tracking-wider">
                  Get Started Free
                </Button>
              </Link>
            </div>

            {/* Pro */}
            <div className="rounded-xl border border-primary/40 bg-primary/5 p-8 space-y-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 px-3 py-1 bg-primary text-primary-foreground text-[10px] font-mono uppercase tracking-wider rounded-bl-lg">
                Most Popular
              </div>
              <div>
                <h3 className="font-mono font-bold uppercase tracking-wider text-primary">Pro</h3>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-4xl font-mono font-bold">$19</span>
                  <span className="text-muted-foreground text-sm">/month</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1 font-mono">Billed monthly, cancel anytime</p>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-2 text-sm text-foreground font-bold">
                  <Star size={14} className="text-amber-400 shrink-0 mt-0.5" fill="currentColor" />
                  <span>Auto-publish: every new episode clipped and posted to your socials. No work after setup.</span>
                </li>
                {[
                  "Auto-posts to YouTube and LinkedIn",
                  "Export portrait, square, and landscape video for TikTok, Reels, Shorts, and X",
                  "Up to 50 hours of episodes per month (up to 2 hours each)",
                  "AI-generated episode artwork (50/month)",
                  "Cinematic social videos (10/month)",
                  "All 3 abbreviation modes (Key Quotes, Quick Catch-Up, Full Recap)",
                  "MP3 download",
                  "Priority processing queue",
                  "Email alerts when each episode is ready",
                  "90-day result storage",
                ].map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Check size={14} className="text-primary shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Link href="/sign-up">
                <Button className="w-full font-mono uppercase text-xs tracking-wider">
                  Start Pro Free Trial
                </Button>
              </Link>
            </div>
          </div>

        </div>
      </section>

      {/* ── Footer CTA ─────────────────────────────────────────────────── */}
      <section className="py-20 border-t border-border/30 bg-primary/3">
        <div className="max-w-2xl mx-auto px-4 text-center space-y-6">
          <h2 className="text-2xl font-mono font-bold tracking-tight">
            Ready to turn every episode into shareable content?
          </h2>
          <p className="text-muted-foreground">
            Join podcasters who publish once and automatically get condensed clips, shareable videos, and social posts. No extra work.
          </p>
          <Link href="/upload">
            <Button size="lg" className="font-mono uppercase tracking-wider">
              Try it on your podcast <ChevronRight size={16} className="ml-1" />
            </Button>
          </Link>
          <p className="text-xs text-muted-foreground font-mono">No credit card · 5 free episodes · Cancel anytime</p>
        </div>
      </section>

      {/* ── Demo mini-player ────────────────────────────────────────────── */}
      {!heroPlayerVisible && !miniPlayerDismissed && (
        <DemoMiniPlayer
          state={demoState}
          onTogglePlay={() => demoPlayerRef.current?.togglePlay()}
          onDismiss={() => {
            sessionStorage.setItem("demoMiniPlayerDismissed", "true");
            setMiniPlayerDismissed(true);
          }}
        />
      )}

      {/* ── Footer ─────────────────────────────────────────────────────── */}
      <footer className="border-t border-border/30 py-8">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-muted-foreground font-mono">
          <div className="flex items-center gap-2">
            <img src={`${basePath}/pa-icon.jpg`} alt="" className="h-4 w-4 rounded-sm object-cover opacity-80" />
            <span className="uppercase tracking-wider">Podcasts <span className="text-primary">Abbreviated</span></span>
          </div>
          <div className="flex items-center gap-6">
            <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link href="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <Link href="/help" className="hover:text-foreground transition-colors">Help</Link>
            <Link href="/sign-in" className="hover:text-foreground transition-colors">Sign In</Link>
            <Link href="/sign-up" className="hover:text-foreground transition-colors">Sign Up</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
